import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GripVertical, ArrowUp, ArrowDown, Save, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/errors";

const move = (arr, from, to) => {
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

function MoveTo({ index, total, onMove, id }) {
  const [v, setV] = useState("");
  const go = () => {
    const n = parseInt(v, 10);
    if (!n || n < 1 || n > total) return toast.error(`Enter a position between 1 and ${total}`);
    onMove(index, n - 1); setV("");
  };
  return (
    <Input value={v} onChange={e => setV(e.target.value.replace(/\D/g, ""))} onKeyDown={e => e.key === "Enter" && go()} onBlur={() => v && go()}
      placeholder="#" className="h-7 w-14 rounded-sm text-xs px-2" data-testid={`reorder-moveto-${id}`} title="Move to position…"/>
  );
}

const LEGACY = [["event", "Event"], ["holder", "Holder"], ["school", "School"], ["year", "Year"], ["result", "Result"]];

const Row = memo(function Row({ r, i, last, cols, isDragging, overDir, flash, h }) {
  return (
    <tr draggable data-testid={`reorder-row-${r.id}`}
      onDragStart={e => { e.dataTransfer.effectAllowed = "move"; h.start(i); }}
      onDragOver={e => { e.preventDefault(); h.over(i); }}
      onDrop={e => { e.preventDefault(); h.drop(i); }}
      onDragEnd={h.end}
      className={`border-b transition-colors duration-150 ${isDragging ? "opacity-40" : ""} ${overDir === "down" ? "border-b-2 border-b-[#E11D48]" : overDir === "up" ? "border-t-2 border-t-[#E11D48]" : ""} ${flash ? "bg-yellow-50" : "hover:bg-slate-50"}`}>
      <td className="pl-3 cursor-grab active:cursor-grabbing text-slate-400" data-testid={`reorder-handle-${r.id}`}><GripVertical className="w-4 h-4"/></td>
      <td className="px-2 py-1.5 font-mono text-xs text-slate-500" data-testid={`reorder-pos-${r.id}`}>{i + 1}</td>
      <td className="px-2 py-1.5 text-xs whitespace-nowrap">{r.category}</td>
      {cols.map(([key, , get]) => <td key={key} className="px-2 py-1.5 whitespace-nowrap max-w-[260px] truncate">{String(get(r) ?? "")}</td>)}
      <td className="px-2 py-1">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={i === 0} onClick={() => h.move(i, i - 1)} data-testid={`reorder-up-${r.id}`}><ArrowUp className="w-3.5 h-3.5"/></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={last} onClick={() => h.move(i, i + 1)} data-testid={`reorder-down-${r.id}`}><ArrowDown className="w-3.5 h-3.5"/></Button>
          <MoveTo index={i} total={h.total()} onMove={h.move} id={r.id}/>
        </div>
      </td>
    </tr>
  );
});

// Full-list drag & drop reorder for one record book (or one event tab).
export default function ReorderPanel({ sportId, recordType, eventColumn, eventValue, columns, onExit, onSaved }) {
  const [rows, setRows] = useState(null);
  const [original, setOriginal] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const flashRef = useRef(null);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    const params = { sport_id: sportId, record_type: recordType };
    if (eventColumn && eventValue) Object.assign(params, { event_column: eventColumn, event_value: eventValue });
    api.get("/records/reorder-list", { params })
      .then(({ data }) => { setRows(data.items); setOriginal(data.items); })
      .catch(e => { toast.error(apiError(e)); onExit(); });
  }, [sportId, recordType, eventColumn, eventValue]);

  const dirty = rows && rows.some((r, i) => r.id !== original[i]?.id);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const dragRef = useRef(null);

  const doMove = useCallback((from, to) => {
    const cur = rowsRef.current;
    if (from === null || from === to || to < 0 || to >= cur.length) return;
    if (cur[from].category !== cur[to].category) return toast.error("Records can only be moved within the same category");
    setRows(move(cur, from, to));
    setFlash(cur[from].id);
    clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setFlash(null), 900);
  }, []);

  const h = useMemo(() => ({
    move: doMove,
    total: () => rowsRef.current?.length || 0,
    start: (i) => { dragRef.current = i; setDragIdx(i); },
    over: (i) => setOverIdx(o => (o === i ? o : i)),
    drop: (i) => { doMove(dragRef.current, i); dragRef.current = null; setDragIdx(null); setOverIdx(null); },
    end: () => { dragRef.current = null; setDragIdx(null); setOverIdx(null); },
  }), [doMove]);

  const cols = useMemo(() => {
    if (!rows) return [];
    const seen = new Set(); const keys = [];
    [...columns, ...rows.flatMap(r => Object.keys(r.dynamic_columns || {}))].forEach(c => { if (c && !seen.has(c)) { seen.add(c); keys.push(c); } });
    if (keys.length) return keys.slice(0, 6).map(c => [c, c, r => r.dynamic_columns?.[c]]);
    return LEGACY.filter(([k]) => rows.some(r => r[k])).map(([k, label]) => [k, label, r => r[k]]);
  }, [rows, columns]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { sport_id: sportId, record_type: recordType, ordered_ids: rows.map(r => r.id) };
      if (eventColumn && eventValue) Object.assign(body, { event_column: eventColumn, event_value: eventValue });
      const { data } = await api.post("/records/reorder", body);
      toast.success(`New order saved (${data.updated} records) — live on the public site`);
      setOriginal(rows); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!rows) return <div className="p-10 text-center text-slate-500" data-testid="reorder-loading">Loading full record book…</div>;

  return (
    <div data-testid="reorder-panel">
      <div className="px-4 py-3 border-b bg-[#0A1128] text-white flex flex-wrap items-center gap-3 sticky top-0 z-10">
        <GripVertical className="w-4 h-4 text-[#FACC15]"/>
        <div className="mr-auto">
          <p className="font-heading tracking-wide">REORDER MODE</p>
          <p className="text-[11px] text-white/70">{recordType}{eventValue ? ` · ${eventValue}` : ""} — {rows.length} records. Drag rows, use the arrows, or type a position.</p>
        </div>
        {dirty && <span className="text-[11px] font-overline text-[#FACC15]" data-testid="reorder-unsaved">Unsaved changes</span>}
        <Button size="sm" variant="ghost" className="rounded-sm text-white hover:bg-white/10" disabled={!dirty} onClick={() => setRows(original)} data-testid="reorder-reset">
          <RotateCcw className="w-4 h-4 mr-1"/>Reset
        </Button>
        <Button size="sm" variant="ghost" className="rounded-sm text-white hover:bg-white/10" onClick={() => (!dirty || window.confirm("Discard unsaved order?")) && onExit()} data-testid="reorder-cancel">
          <X className="w-4 h-4 mr-1"/>Exit
        </Button>
        <Button size="sm" className="rounded-sm bg-[#E11D48] hover:bg-[#be123c] font-heading" disabled={!dirty || saving} onClick={save} data-testid="reorder-save">
          <Save className="w-4 h-4 mr-1"/>{saving ? "SAVING…" : "SAVE ORDER"}
        </Button>
      </div>
      {rows.length === 0 ? <p className="p-10 text-center text-slate-500" data-testid="reorder-empty">No records you can reorder here.</p> : (
        <div className="max-h-[640px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0 z-[1]">
              <tr className="text-left">
                <th className="w-10"/><th className="px-2 py-2 w-12 font-heading">#</th>
                <th className="px-2 py-2 font-heading whitespace-nowrap">Category</th>
                {cols.map(([key, label]) => <th key={key} className="px-2 py-2 font-heading whitespace-nowrap">{label}</th>)}
                <th className="px-2 py-2 w-40 text-right font-heading">Move</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <Row key={r.id} r={r} i={i} last={i === rows.length - 1} cols={cols} h={h} flash={flash === r.id}
                  isDragging={dragIdx === i}
                  overDir={overIdx === i && dragIdx !== null && dragIdx !== i ? (dragIdx < i ? "down" : "up") : null}/>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
